import React, { ReactNode } from "react";
import { ErrorBoundary as ReactErrorBoundary, FallbackProps } from "react-error-boundary";
import { AlertTriangle, RefreshCw, Home } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { captureError } from "@/lib/sentry";

interface ErrorBoundaryProps {
  children: ReactNode;
  fallback?: ReactNode;
  onError?: (error: Error, errorInfo: React.ErrorInfo) => void;
  onReset?: () => void;
}

const DefaultErrorFallback = ({ error, resetErrorBoundary }: FallbackProps) => {
  const handleReload = () => {
    window.location.reload();
  };

  const handleGoHome = () => {
    window.location.href = "/";
  };

  return (
    <div className="flex items-center justify-center min-h-screen bg-background p-4">
      <div className="w-full max-w-2xl space-y-6">
        <Alert variant="destructive" className="border-2">
          <AlertTriangle className="h-5 w-5" />
          <AlertTitle className="text-xl font-bold">
            Something went wrong
          </AlertTitle>
          <AlertDescription className="mt-2">
            An unexpected error occurred. You can try refreshing the page or
            returning to the home screen.
          </AlertDescription>
        </Alert>

        <div className="flex gap-3 flex-wrap">
          <Button onClick={resetErrorBoundary} variant="default" size="lg">
            <RefreshCw className="mr-2 h-4 w-4" />
            Try Again
          </Button>
          <Button onClick={handleReload} variant="outline" size="lg">
            <RefreshCw className="mr-2 h-4 w-4" />
            Reload Page
          </Button>
          <Button onClick={handleGoHome} variant="outline" size="lg">
            <Home className="mr-2 h-4 w-4" />
            Go Home
          </Button>
        </div>

        {process.env.NODE_ENV === "development" && error && (
          <details className="mt-6 p-4 bg-muted rounded-lg border">
            <summary className="cursor-pointer font-semibold mb-2">
              Error Details (Development Only)
            </summary>
            <div className="space-y-2 text-sm">
              <div>
                <strong>Error:</strong>
                <pre className="mt-1 p-2 bg-background rounded overflow-x-auto">
                  {error.toString()}
                </pre>
              </div>
              {error.stack && (
                <div>
                  <strong>Stack Trace:</strong>
                  <pre className="mt-1 p-2 bg-background rounded overflow-x-auto text-xs">
                    {error.stack}
                  </pre>
                </div>
              )}
            </div>
          </details>
        )}
      </div>
    </div>
  );
};

const ErrorBoundary = ({ children, fallback, onError, onReset }: ErrorBoundaryProps) => {
  const handleError = (error: Error, errorInfo: React.ErrorInfo) => {
    console.error("Error caught by ErrorBoundary:", error, errorInfo);
    
    // Report to Sentry with React component stack
    captureError(error, {
      componentStack: errorInfo.componentStack,
      errorBoundary: true,
    });
    
    onError?.(error, errorInfo);
  };

  return (
    <ReactErrorBoundary
      FallbackComponent={fallback ? () => <>{fallback}</> : DefaultErrorFallback}
      onError={handleError}
      onReset={onReset}
    >
      {children}
    </ReactErrorBoundary>
  );
};

export default ErrorBoundary;
